/**
 * @copyright
 * Copyright(c) 2020 SICK AG
 */

/**
 * Retrieves a reference to the decimal data-series values of the given sample.
 * Creates an empty array if no decimal data-series values are defined.
 */
export function getDecimalDataSeriesValues(sample) {
  var _ref, _sample$decimalValues;

  return (_ref = (_sample$decimalValues = sample.decimalValues) !== null && _sample$decimalValues !== void 0 ? _sample$decimalValues : sample.values) !== null && _ref !== void 0 ? _ref : [];
}
/**
 * Retrieves a reference to the enum data-series values of the given sample.
 * Creates an empty array if no enum data-series values are defined.
 */

export function getEnumDataSeriesValues(sample) {
  var _sample$enumValues;

  return (_sample$enumValues = sample.enumValues) !== null && _sample$enumValues !== void 0 ? _sample$enumValues : [];
}
/**
 * Retrieves the number of decimal data-series from the given sample.
 */

export function getDecimalDataSeriesCount(sample) {
  return getDecimalDataSeriesValues(sample).length;
}
/**
 * Retrieves the number of enum data-series from the given sample.
 */

export function getEnumDataSeriesCount(sample) {
  return getEnumDataSeriesValues(sample).length;
}
/**
 * Type-guard for {@link QuickChart.SamplingPeriod}
 */

export function isSamplingPeriod(samplingPeriod) {
  switch (samplingPeriod) {
    case 'EveryMillisecond':
    case 'EveryTenMillis':
    case 'EveryHundredMillis':
    case 'EverySecond':
    case 'EveryTenSeconds':
    case 'EveryMinute':
    case 'EveryTenMinutes':
    case 'EveryHour':
    case 'Every6Hours':
    case 'Every24Hours':
      return true;

    default:
      return false;
  }
}
/**
 * Computes the milliseconds for the given sampling-period
 */

export function samplingPeriodToMillis(samplingPeriod) {
  switch (samplingPeriod) {
    case 'EveryMillisecond':
      return 1;

    case 'EveryTenMillis':
      return 10;

    case 'EveryHundredMillis':
      return 100;

    case 'EverySecond':
      return 1000;

    case 'EveryTenSeconds':
      return 10 * 1000;

    case 'EveryMinute':
      return 60 * 1000;

    case 'EveryTenMinutes':
      return 10 * 60 * 1000;

    case 'EveryHour':
      return 60 * 60 * 1000;

    case 'Every6Hours':
      return 6 * 60 * 60 * 1000;

    case 'Every24Hours':
      return 24 * 60 * 60 * 1000;

    default:
      throw new Error(`unsupported sampling period <${samplingPeriod}>`);
  }
}
/**
 * Creates a legend state.
 *
 * @param legendItemIndexOffset - an offset to be added to the index of the legend-item; does not effect the {@link visibleDataSeriesIndices}
 * @param length - the number of legend-items to be created.
 * @param visibleDataSeriesIndices - the indices of the visible data-series; undefined implies all data-series are visible; this argument will determine the active state of the legend-items.
 */

export function createLegendState(legendItemIndexOffset, length, visibleDataSeriesIndices) {
  const allDataSeriesAreVisible = visibleDataSeriesIndices === undefined;
  const legendState = Array.from({
    length: length
  }, (_, i) => ({
    index: legendItemIndexOffset + i,
    active: allDataSeriesAreVisible
  }));

  if (!allDataSeriesAreVisible) {
    visibleDataSeriesIndices.forEach(visibleDataSeriesIndex => {
      if (legendState !== null && legendState !== void 0 && legendState[visibleDataSeriesIndex]) {
        legendState[visibleDataSeriesIndex].active = true;
      }
    });
  }

  return legendState;
}
/**
 * Computes the indices of all active legend items.
 */

export function computeActiveLegendItemIndices(legendState) {
  return legendState.filter(ls => ls.active).map(ls => ls.index);
}
/**
 * Computes the colors to be used for the legend of the given plotter.
 */

export function createLegendColors(plotter) {
  const dataSeriesColors = plotter.legendLabels.map((_legendLabel, index) => {
    var _plotter$dataSeriesCo, _plotter$dataSeriesCo2;

    return (_plotter$dataSeriesCo = (_plotter$dataSeriesCo2 = plotter.dataSeriesConfigs[index]) === null || _plotter$dataSeriesCo2 === void 0 ? void 0 : _plotter$dataSeriesCo2.lineColor) !== null && _plotter$dataSeriesCo !== void 0 ? _plotter$dataSeriesCo : index;
  });

  if (plotter.interactiveLegend) {
    const enumSeriesColors = plotter.enumLegendLabels.map((_legendLabel, _index) => 'white');
    return [...dataSeriesColors, ...enumSeriesColors];
  } // The enum data-series do not need to be part of the legend if the legend is not interactive.


  return dataSeriesColors;
}
/**
 * Computes the labels to be shown in the legend of the given plotter.
 */

export function createLegendLabels(plotter) {
  if (plotter.interactiveLegend) {
    return [...plotter.legendLabels, ...plotter.enumLegendLabels.map((label, index) => {
      var _plotter$enumDataSeri;

      return label !== null && label !== void 0 ? label : (_plotter$enumDataSeri = plotter.enumDataSeriesConfigs[index]) === null || _plotter$enumDataSeri === void 0 ? void 0 : _plotter$enumDataSeri.label;
    })];
  } // The enum data-series do not need to be part of the legend if the legend is not interactive.


  return plotter.legendLabels;
}
/**
 * Create legend-types specific to decimal and enum series
 */

export function createLegendTypes(plotter) {
  if (plotter.interactiveLegend) {
    return [...plotter.legendLabels.map(() => 'symbol'), ...plotter.enumLegendLabels.map(() => 'button')];
  } // The enum data-series do not need to be part of the legend if the legend is not interactive.


  return [];
}
/**
 * Computes the symbols to be shown in the legend of the given plotter.
 */

export function createLegendSymbols(plotter) {
  if (plotter.interactiveLegend) {
    return [...plotter.legendLabels.map(_ => 'line'), ...plotter.enumLegendLabels.map(_ => 'nothing')];
  } // The enum data-series do not need to be part of the legend if the legend is not interactive.


  return ['line'];
}
